import { useSearchParams } from 'react-router-dom';
import {
    fetchVideoDetails,
    fetchVideoPlaybackSource,
    resolvePlaybackQualityRequest,
    searchVideos,
    type VideoPlaybackQuality
} from '../../../services/video.service';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Video, VideoPlaybackSource } from '../../../types/video.types';
import { useUserState } from "../../../providers/user-state.provider.ts";
import { Routes } from "../../../types/constants";
import router from "../../../providers/router";
import VideoPlayer from '../../VideoPlayer/VideoPlayer';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RelatedVideos from '../../RelatedVideos/RelatedVideos';
import { VideoCardData } from '../../../types/video-card.types';
import {
    buildVideoCardData,
    formatVideoCardDate,
    formatVideoCardFightNumber,
    formatVideoCardParticipantName,
    orderVideoCardParticipants
} from '../../../utils/video-card';
import FighterCornerBars from '../../FighterCornerBars/FighterCornerBars';
import { useAuthModal } from "../../../providers/auth-modal.provider";
import { usePurchaseStatus } from "../../../providers/purchase-status.provider";
import { usePurchaseFlow } from '../../../hooks/usePurchaseFlow';
import { useSaveVideo } from '../../../hooks/useSaveVideo';
import { useAnalyticsService } from '../../../services/analytics.service';
import { ANALYTICS_EVENT_TYPES, type AnalyticsEventType } from '../../../types/analytics.types';
import { isAdmin } from '../../../utils/roles';
import SearchDiscoveryPanels, { DiscoveryTag } from '../../Search/SearchDiscoveryPanels';
import { buildTagResultsRoute, buildTextResultsRoute } from '../../../utils/search-routes';
import { useContinueWatching } from '../../../hooks/useContinueWatching';
import WatchMomentumPanel from '../../WatchMomentum/WatchMomentumPanel';
import styles from './WatchPage.module.scss';
import { fetchPublicSiteConfiguration } from '../../../services/marketing.service';
import { useSubscriptionStatus } from '../../../providers/subscription-status.provider';
import { buildJoinUrl } from '../../../utils/join';

const UP_NEXT_LIMIT = 12;
const UP_NEXT_PAGE_SIZE = 20;
const MAX_TAG_QUERY_COUNT = 5;
const MAX_PARTICIPANT_QUERY_COUNT = 2;
const WATCH_START_SECONDS = 10;
const WATCH_PROGRESS_INTERVAL = 30;
const PLAYBACK_SOURCE_REFRESH_BUFFER_MS = 120_000;
type FloatingPlayerMode = 'none' | 'desktop' | 'mobile';
type PlaybackSourceStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error' | 'denied';
type PendingSeekReason = 'resume' | 'recovery';

interface PlaybackPerformanceState {
    startupStartedAtMs: number | null;
    firstPlayingAtMs: number | null;
    bufferStartedAtMs: number | null;
    seekingStartedAtMs: number | null;
    bufferCount: number;
    totalBufferMs: number;
    maxBufferMs: number;
}

interface PlaybackSourceDeniedState {
    videoId: string;
    entitlementMarker: string | null;
}

const isValidGuid = (value?: string | null) => Boolean(value && /^[0-9a-fA-F-]{36}$/.test(value));
const nowMs = () => (
    typeof globalThis.performance?.now === 'function'
        ? globalThis.performance.now()
        : Date.now()
);
const roundMetric = (value: number) => Math.max(0, Math.round(value));
const createPlaybackPerformanceState = (): PlaybackPerformanceState => ({
    startupStartedAtMs: null,
    firstPlayingAtMs: null,
    bufferStartedAtMs: null,
    seekingStartedAtMs: null,
    bufferCount: 0,
    totalBufferMs: 0,
    maxBufferMs: 0
});
const getPlaybackMediaQuery = () => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 1023px)')
        : null
);
const resolvePlaybackQuality = (): VideoPlaybackQuality | undefined => (
    getPlaybackMediaQuery()?.matches ? 'mobile' : undefined
);
const parseResumeSeconds = (value?: string | null) => {
    if (!value) {
        return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return null;
    }

    return parsed;
};
const readResumeSecondsFromWatchUrl = (watchUrl?: string | null) => {
    if (!watchUrl) {
        return null;
    }

    const queryIndex = watchUrl.indexOf('?');
    if (queryIndex < 0) {
        return null;
    }

    return parseResumeSeconds(new URLSearchParams(watchUrl.slice(queryIndex + 1)).get('t'));
};
const readHttpStatus = (error: unknown) => (
    typeof error === 'object' && error !== null && 'status' in error
        ? Number((error as { status?: number }).status)
        : undefined
);

const inferPlaybackFileFormat = (
    source: VideoPlaybackSource | null,
    quality: string
) => {
    if (source?.FileFormat) {
        return source.FileFormat;
    }

    if (source?.ContentType?.toLowerCase() === 'video/webm') {
        return 'Webm';
    }

    return quality === 'mobile' ? 'Mp4Mobile' : 'Mp4';
};
const parsePlaybackSourceExpiryMs = (source?: VideoPlaybackSource | null) => {
    if (!source?.ExpiresAtUtc) {
        return null;
    }

    const expiryMs = Date.parse(source.ExpiresAtUtc);
    return Number.isFinite(expiryMs) ? expiryMs : null;
};
const isPlaybackSourceExpired = (source?: VideoPlaybackSource | null) => {
    const expiryMs = parsePlaybackSourceExpiryMs(source);
    return expiryMs !== null && expiryMs <= Date.now();
};
const buildEntitlementMarker = (
    token: string | null | undefined,
    roles: string[] | undefined,
    hasActiveSubscription: boolean,
    ownsVideo: boolean
) => [
    token ?? '',
    [...(roles ?? [])].sort().join(','),
    hasActiveSubscription ? 'subscribed' : 'not-subscribed',
    ownsVideo ? 'owned' : 'not-owned'
].join('|');

const WatchPage = () => {
    const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
    const [relatedVideos, setRelatedVideos] = useState<Video[]>([]);
    const [isRelatedLoading, setIsRelatedLoading] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [isCheckingAccess, setIsCheckingAccess] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const currentVideoShortId = searchParams.get('v');
    const [currentVideoId, setCurrentVideoId] = useState<string | null>(currentVideoShortId);
    const { isLoggedIn, isUserStateLoading, getUserInfo } = useUserState();
    const currentUserInfo = getUserInfo();
    const playbackToken = currentUserInfo?.token ?? null;
    const playbackUserRolesKey = [...(currentUserInfo?.roles ?? [])].sort().join('|');
    const playbackContextKey = `${playbackToken ?? ''}|${currentVideoId ?? ''}`;
    const { openModal } = useAuthModal();
    const { isPurchaseLoading, isVideoOwned } = usePurchaseStatus();
    const { hasActiveSubscription, isSubscriptionLoading } = useSubscriptionStatus();
    const { startPurchase } = usePurchaseFlow();
    const { toggleSaved, isSaved } = useSaveVideo();
    const { trackEvent, trackWatchProgress, getSessionId } = useAnalyticsService();
    const analyticsSessionId = useMemo(() => getSessionId(), [getSessionId]);
    const watchStateRef = useRef({ hasStarted: false, lastProgressBucket: 0, durationSeconds: 0 });
    const playbackPerformanceRef = useRef<PlaybackPerformanceState>(createPlaybackPerformanceState());
    const analyticsVideoIdRef = useRef<string | null>(null);
    const pendingSeekSecondsRef = useRef<number | null>(null);
    const pendingSeekReasonRef = useRef<PendingSeekReason | null>(null);
    const playbackSourceDeniedRef = useRef<PlaybackSourceDeniedState | null>(null);
    const entitlementMarkerRef = useRef<string | null>(null);
    const refreshedPlaybackSourceRef = useRef<VideoPlaybackSource | null>(null);
    const playbackSourceRecoveryRef = useRef(false);
    const playbackSourceRequestIdRef = useRef(0);
    const accessRequestIdRef = useRef(0);
    const resumeAfterSourceRefreshRef = useRef(false);
    const playerStageRef = useRef<HTMLDivElement | null>(null);
    const [floatingPlayerMode, setFloatingPlayerMode] = useState<FloatingPlayerMode>('none');
    const [isMiniPlayerDismissed, setIsMiniPlayerDismissed] = useState(false);
    const [playbackQuality, setPlaybackQuality] = useState<VideoPlaybackQuality | undefined>(() => resolvePlaybackQuality());
    const [isMobileVideoPlaybackEnabled, setIsMobileVideoPlaybackEnabled] = useState(false);
    const [playbackSource, setPlaybackSource] = useState<VideoPlaybackSource | null>(null);
    const [playbackSourceContextKey, setPlaybackSourceContextKey] = useState<string | null>(null);
    const [playbackSourceStatus, setPlaybackSourceStatus] = useState<PlaybackSourceStatus>('idle');
    const [playbackSourceMessage, setPlaybackSourceMessage] = useState<string | null>(null);
    const [canRestartFromResume, setCanRestartFromResume] = useState(false);
    const [isVideoPlaybackActive, setIsVideoPlaybackActive] = useState(false);
    const searchQuery = searchParams.get('q')?.trim() ?? '';
    const searchTags = useMemo(
        () => searchParams.getAll('tags').map((tag) => tag.trim()).filter((tag) => tag.length > 0),
        [searchParams]
    );
    const hasSearchContext = searchQuery.length > 0 || searchTags.length > 0;
    const resumeSecondsParam = searchParams.get('t');
    const continueWatching = useContinueWatching({
        limit: 4,
        excludeVideoUrl: selectedVideo?.Url,
        excludeVideoId: selectedVideo?.Id
    });
    const ownsSelectedVideo = selectedVideo?.Id ? isVideoOwned(selectedVideo.Id) : false;
    const accessDecisionKey = [
        playbackContextKey,
        playbackUserRolesKey,
        hasActiveSubscription ? 'subscribed' : 'not-subscribed',
        ownsSelectedVideo ? 'owned' : 'not-owned'
    ].join('|');
    const accessDecisionKeyRef = useRef(accessDecisionKey);
    accessDecisionKeyRef.current = accessDecisionKey;
    const [accessContextKey, setAccessContextKey] = useState(accessDecisionKey);
    const hasCurrentAccessState = accessContextKey === accessDecisionKey;
    const currentHasAccess = hasCurrentAccessState
        && !isPurchaseLoading
        && !isSubscriptionLoading
        && hasAccess;
    const currentIsCheckingAccess = !hasCurrentAccessState
        || isCheckingAccess
        || isPurchaseLoading
        || isSubscriptionLoading;

    const combinedTags = useMemo(() => {
        if (!selectedVideo) {
            return [];
        }
        return Array.from(new Set(
            [...(selectedVideo.EventTags ?? []), ...(selectedVideo.Tags ?? [])]
                .map((tag) => tag.trim())
                .filter((tag) => tag.length > 0)
        ));
    }, [selectedVideo]);
    const watchDiscoveryTags = useMemo<DiscoveryTag[]>(
        () => combinedTags.map((tag) => ({
            id: `watch-tag-${tag.toLowerCase()}`,
            name: tag
        })),
        [combinedTags]
    );

    const participantNames = useMemo(() => {
        if (!selectedVideo) {
            return [];
        }
        const names = selectedVideo.Participants?.map((participant) => participant.ParticipantName) ?? [];
        const unique = new Map<string, string>();
        names.forEach((name) => {
            const normalized = name?.trim().toLowerCase();
            if (normalized && !unique.has(normalized)) {
                unique.set(normalized, name.trim());
            }
        });
        return Array.from(unique.values());
    }, [selectedVideo]);

    const requestedPlaybackQuality = useMemo(
        () => resolvePlaybackQualityRequest(
            playbackQuality,
            isMobileVideoPlaybackEnabled,
            selectedVideo?.MobilePlaybackOverride ?? null
        ),
        [isMobileVideoPlaybackEnabled, playbackQuality, selectedVideo?.MobilePlaybackOverride]
    );

    const currentPlaybackSource = playbackSourceContextKey === accessDecisionKey ? playbackSource : null;
    const currentPlaybackSourceStatus = playbackSourceContextKey === accessDecisionKey
        ? playbackSourceStatus
        : 'idle';
    const currentPlaybackSourceMessage = playbackSourceContextKey === accessDecisionKey
        ? playbackSourceMessage
        : null;
    const playerVideoUrl = currentPlaybackSource?.Url ?? '';
    const playerContentType = currentPlaybackSource?.ContentType ?? 'video/mp4';
    const playbackTransport = currentPlaybackSource?.DeliveryMode ?? 'api_stream';
    const resolvedPlaybackQualityLabel = currentPlaybackSource?.Quality ?? requestedPlaybackQuality ?? 'default';
    const playbackFileFormat = inferPlaybackFileFormat(currentPlaybackSource, resolvedPlaybackQualityLabel);

    useEffect(() => {
        const selectedVideoId = selectedVideo?.Id;
        const isGuid = isValidGuid(selectedVideoId);
        analyticsVideoIdRef.current = isGuid && selectedVideoId ? selectedVideoId : null;
        watchStateRef.current = { hasStarted: false, lastProgressBucket: 0, durationSeconds: 0 };
        const resumeSeconds = parseResumeSeconds(resumeSecondsParam);
        pendingSeekSecondsRef.current = resumeSeconds;
        pendingSeekReasonRef.current = resumeSeconds !== null && resumeSeconds > 0 ? 'resume' : null;
        setCanRestartFromResume(false);
        setIsVideoPlaybackActive(false);
    }, [resumeSecondsParam, selectedVideo?.Id]);

    useEffect(() => {
        playbackSourceDeniedRef.current = null;
        setCurrentVideoId(currentVideoShortId);
    }, [currentVideoShortId]);

    useEffect(() => {
        playbackSourceDeniedRef.current = null;
    }, [playbackToken, playbackUserRolesKey]);

    useEffect(() => {
        setFloatingPlayerMode('none');
        setIsMiniPlayerDismissed(false);
    }, [currentVideoId]);

    useEffect(() => {
        playbackPerformanceRef.current = createPlaybackPerformanceState();
        setIsVideoPlaybackActive(false);
    }, [playerVideoUrl]);

    const loadPlaybackSource = useCallback(async (options: {
        background?: boolean;
        recoverFromSeconds?: number | null;
        resumeAfterLoad?: boolean;
    } = {}) => {
        if (!currentVideoId || !currentHasAccess) {
            return null;
        }

        const requestedPlaybackContextKey = accessDecisionKey;
        const requestId = playbackSourceRequestIdRef.current + 1;
        playbackSourceRequestIdRef.current = requestId;

        if (!options.background) {
            setPlaybackSource(null);
            setPlaybackSourceContextKey(requestedPlaybackContextKey);
            setPlaybackSourceStatus('loading');
            setPlaybackSourceMessage(null);
        }

        try {
            const source = await fetchVideoPlaybackSource(currentVideoId, requestedPlaybackQuality, playbackToken);
            if (playbackSourceRequestIdRef.current !== requestId
                || accessDecisionKeyRef.current !== requestedPlaybackContextKey) {
                return null;
            }

            if (options.background) {
                refreshedPlaybackSourceRef.current = source;
                return source;
            }

            playbackSourceDeniedRef.current = null;
            refreshedPlaybackSourceRef.current = null;
            if (Number.isFinite(options.recoverFromSeconds) && (options.recoverFromSeconds ?? 0) > 0) {
                pendingSeekSecondsRef.current = options.recoverFromSeconds!;
                pendingSeekReasonRef.current = 'recovery';
            }
            resumeAfterSourceRefreshRef.current = options.resumeAfterLoad === true;
            setPlaybackSource(source);
            setPlaybackSourceContextKey(requestedPlaybackContextKey);
            setPlaybackSourceStatus('ready');
            setPlaybackSourceMessage(null);
            return source;
        } catch (error) {
            if (playbackSourceRequestIdRef.current !== requestId
                || accessDecisionKeyRef.current !== requestedPlaybackContextKey) {
                return null;
            }

            if (options.background) {
                return null;
            }

            const status = readHttpStatus(error);
            setPlaybackSource(null);

            if (status === 401 || status === 403) {
                playbackSourceDeniedRef.current = {
                    videoId: currentVideoId,
                    entitlementMarker: entitlementMarkerRef.current
                };
                setPlaybackSourceStatus('denied');
                setPlaybackSourceMessage(null);
                setHasAccess(false);
                return null;
            }

            setPlaybackSourceStatus(status === 404 ? 'unavailable' : 'error');
            setPlaybackSourceMessage(
                status === 404
                    ? 'Video is not available for playback yet.'
                    : 'Unable to prepare video. Please try again.'
            );
            return null;
        }
    }, [accessDecisionKey, currentHasAccess, currentVideoId, playbackToken, requestedPlaybackQuality]);

    const recoverPlaybackSource = useCallback(async (
        video: HTMLVideoElement,
        resumeAfterLoad: boolean
    ) => {
        if (playbackSourceRecoveryRef.current || !currentVideoId || !currentHasAccess) {
            return;
        }

        playbackSourceRecoveryRef.current = true;
        const recoverFromSeconds = Number.isFinite(video.currentTime) ? video.currentTime : null;
        const refreshedSource = refreshedPlaybackSourceRef.current;

        try {
            if (refreshedSource && !isPlaybackSourceExpired(refreshedSource)) {
                pendingSeekSecondsRef.current = recoverFromSeconds && recoverFromSeconds > 0 ? recoverFromSeconds : null;
                pendingSeekReasonRef.current = recoverFromSeconds && recoverFromSeconds > 0 ? 'recovery' : null;
                resumeAfterSourceRefreshRef.current = resumeAfterLoad;
                refreshedPlaybackSourceRef.current = null;
                setPlaybackSource(refreshedSource);
                setPlaybackSourceContextKey(accessDecisionKey);
                setPlaybackSourceStatus('ready');
                setPlaybackSourceMessage(null);
                return;
            }

            await loadPlaybackSource({ recoverFromSeconds, resumeAfterLoad });
        } finally {
            playbackSourceRecoveryRef.current = false;
        }
    }, [accessDecisionKey, currentHasAccess, currentVideoId, loadPlaybackSource]);

    useEffect(() => {
        playbackSourceRequestIdRef.current += 1;
        setPlaybackSource(null);
        setPlaybackSourceStatus('idle');
        setPlaybackSourceMessage(null);
        refreshedPlaybackSourceRef.current = null;

        if (!currentVideoId || !currentHasAccess) {
            return;
        }

        void loadPlaybackSource();
    }, [currentHasAccess, currentVideoId, loadPlaybackSource]);

    useEffect(() => {
        const expiryMs = parsePlaybackSourceExpiryMs(currentPlaybackSource);
        if (!currentHasAccess || expiryMs === null) {
            return;
        }

        const delayMs = Math.max(0, expiryMs - Date.now() - PLAYBACK_SOURCE_REFRESH_BUFFER_MS);
        const timeoutId = window.setTimeout(() => {
            void loadPlaybackSource({ background: true });
        }, delayMs);

        return () => window.clearTimeout(timeoutId);
    }, [currentHasAccess, currentPlaybackSource, loadPlaybackSource]);

    useEffect(() => {
        const mediaQuery = getPlaybackMediaQuery();
        if (!mediaQuery) {
            return;
        }

        const updatePlaybackQuality = () => setPlaybackQuality(mediaQuery.matches ? 'mobile' : undefined);

        updatePlaybackQuality();
        mediaQuery.addEventListener('change', updatePlaybackQuality);
        return () => mediaQuery.removeEventListener('change', updatePlaybackQuality);
    }, []);

    useEffect(() => {
        let isMounted = true;

        fetchPublicSiteConfiguration()
            .then((siteConfig) => {
                if (isMounted) {
                    setIsMobileVideoPlaybackEnabled(siteConfig?.EnableMobileVideoPlayback === true);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setIsMobileVideoPlaybackEnabled(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!currentHasAccess || isMiniPlayerDismissed) {
            setFloatingPlayerMode('none');
            return;
        }

        const updateFloatingState = () => {
            const playerBounds = playerStageRef.current?.getBoundingClientRect();
            if (!playerBounds) {
                setFloatingPlayerMode('none');
                return;
            }

            const viewportWidth = window.innerWidth;
            const headerOffset = viewportWidth <= 480 ? 56 : viewportWidth <= 900 ? 60 : 72;

            if (viewportWidth < 1024) {
                const hasScrolledPastPlayer = playerBounds.top <= headerOffset;
                setFloatingPlayerMode(hasScrolledPastPlayer ? 'mobile' : 'none');
                return;
            }

            if (viewportWidth >= 1024) {
                setFloatingPlayerMode(playerBounds.bottom <= 96 ? 'desktop' : 'none');
                return;
            }

            setFloatingPlayerMode('none');
        };

        updateFloatingState();
        window.addEventListener('scroll', updateFloatingState, { passive: true });
        window.addEventListener('resize', updateFloatingState);

        return () => {
            window.removeEventListener('scroll', updateFloatingState);
            window.removeEventListener('resize', updateFloatingState);
        };
    }, [currentHasAccess, isMiniPlayerDismissed]);

    const buildPlaybackPerformanceMetadata = useCallback((
        video?: HTMLVideoElement,
        extra: Record<string, string | number | boolean | null | undefined> = {}
    ) => {
        const metrics = playbackPerformanceRef.current;
        const startupMs = metrics.firstPlayingAtMs === null || metrics.startupStartedAtMs === null
            ? undefined
            : roundMetric(metrics.firstPlayingAtMs - metrics.startupStartedAtMs);

        return {
            playbackTransport,
            quality: resolvedPlaybackQualityLabel,
            fileFormat: playbackFileFormat,
            isMobileViewport: playbackQuality === 'mobile',
            mobilePlaybackEnabled: isMobileVideoPlaybackEnabled,
            startupMs,
            bufferCount: metrics.bufferCount,
            totalBufferMs: roundMetric(metrics.totalBufferMs),
            maxBufferMs: roundMetric(metrics.maxBufferMs),
            readyState: video?.readyState,
            networkState: video?.networkState,
            ...extra
        };
    }, [isMobileVideoPlaybackEnabled, playbackFileFormat, playbackQuality, playbackTransport, resolvedPlaybackQualityLabel]);

    const trackPlaybackEvent = useCallback((
        eventType: AnalyticsEventType,
        video: HTMLVideoElement,
        extraMetadata: Record<string, string | number | boolean | null | undefined> = {}
    ) => {
        const videoId = analyticsVideoIdRef.current;
        if (!videoId) {
            return;
        }

        const duration = Number.isFinite(video.duration)
            ? video.duration
            : watchStateRef.current.durationSeconds;

        void trackEvent({
            eventType,
            videoId,
            positionSeconds: Number.isFinite(video.currentTime) ? video.currentTime : undefined,
            durationSeconds: duration || undefined,
            sessionId: analyticsSessionId,
            path: `${window.location.pathname}${window.location.search}`,
            metadata: buildPlaybackPerformanceMetadata(video, extraMetadata)
        });
    }, [analyticsSessionId, buildPlaybackPerformanceMetadata, trackEvent]);

    const handleLoadedMetadata = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const duration = event.currentTarget.duration;
        if (Number.isFinite(duration) && duration > 0) {
            watchStateRef.current.durationSeconds = duration;
        }

        if (pendingSeekSecondsRef.current !== null) {
            const requestedSeek = pendingSeekSecondsRef.current;
            const pendingSeekReason = pendingSeekReasonRef.current;
            const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : undefined;
            const seekTo = safeDuration ? Math.min(requestedSeek, safeDuration) : requestedSeek;

            event.currentTarget.currentTime = seekTo;
            watchStateRef.current.lastProgressBucket = Math.floor(seekTo / WATCH_PROGRESS_INTERVAL);
            pendingSeekSecondsRef.current = null;
            pendingSeekReasonRef.current = null;
            if (pendingSeekReason === 'resume' && seekTo > 0) {
                setCanRestartFromResume(true);
            }
        }

        if (resumeAfterSourceRefreshRef.current) {
            resumeAfterSourceRefreshRef.current = false;
            void event.currentTarget.play().catch(() => undefined);
        }
    }, []);

    const handleTimeUpdate = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const videoId = analyticsVideoIdRef.current;
        if (!videoId) {
            return;
        }

        const currentTime = event.currentTarget.currentTime;
        if (!Number.isFinite(currentTime) || currentTime <= 0) {
            return;
        }

        const duration = Number.isFinite(event.currentTarget.duration)
            ? event.currentTarget.duration
            : watchStateRef.current.durationSeconds;

        if (duration && duration > 0 && watchStateRef.current.durationSeconds !== duration) {
            watchStateRef.current.durationSeconds = duration;
        }

        const path = `${window.location.pathname}${window.location.search}`;

        if (!watchStateRef.current.hasStarted && currentTime >= WATCH_START_SECONDS) {
            watchStateRef.current.hasStarted = true;
            watchStateRef.current.lastProgressBucket = Math.floor(currentTime / WATCH_PROGRESS_INTERVAL);
            void trackWatchProgress({
                eventType: ANALYTICS_EVENT_TYPES.VIDEO_WATCH_START,
                videoId,
                positionSeconds: currentTime,
                durationSeconds: duration || undefined,
                sessionId: analyticsSessionId,
                path,
                metadata: buildPlaybackPerformanceMetadata(event.currentTarget)
            });
        }

        if (watchStateRef.current.hasStarted) {
            const bucket = Math.floor(currentTime / WATCH_PROGRESS_INTERVAL);
            if (bucket > watchStateRef.current.lastProgressBucket) {
                watchStateRef.current.lastProgressBucket = bucket;
                void trackWatchProgress({
                    eventType: ANALYTICS_EVENT_TYPES.VIDEO_WATCH_PROGRESS,
                    videoId,
                    positionSeconds: currentTime,
                    durationSeconds: duration || undefined,
                    sessionId: analyticsSessionId,
                    path
                });
            }
        }
    }, [analyticsSessionId, buildPlaybackPerformanceMetadata, trackWatchProgress]);

    const handleEnded = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        setIsVideoPlaybackActive(false);
        const videoId = analyticsVideoIdRef.current;
        if (!videoId) {
            return;
        }

        const duration = Number.isFinite(event.currentTarget.duration)
            ? event.currentTarget.duration
            : watchStateRef.current.durationSeconds;

        if (duration && duration > 0) {
            watchStateRef.current.durationSeconds = duration;
        }

        void trackWatchProgress({
            eventType: ANALYTICS_EVENT_TYPES.VIDEO_WATCH_COMPLETE,
            videoId,
            positionSeconds: duration || event.currentTarget.currentTime,
            durationSeconds: duration || undefined,
            sessionId: analyticsSessionId,
            path: `${window.location.pathname}${window.location.search}`,
            metadata: buildPlaybackPerformanceMetadata(event.currentTarget)
        });
    }, [analyticsSessionId, buildPlaybackPerformanceMetadata, trackWatchProgress]);

    const handlePlaying = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        setIsVideoPlaybackActive(true);
        const metrics = playbackPerformanceRef.current;
        const timestamp = nowMs();
        if (metrics.firstPlayingAtMs === null) {
            metrics.startupStartedAtMs ??= timestamp;
            metrics.firstPlayingAtMs = timestamp;
            const startupMs = roundMetric(timestamp - metrics.startupStartedAtMs);
            trackPlaybackEvent(
                ANALYTICS_EVENT_TYPES.VIDEO_PLAYBACK_STARTUP,
                event.currentTarget,
                { startupMs }
            );
        }

        if (metrics.bufferStartedAtMs === null) {
            return;
        }

        const bufferMs = roundMetric(timestamp - metrics.bufferStartedAtMs);
        metrics.bufferStartedAtMs = null;
        metrics.bufferCount += 1;
        metrics.totalBufferMs += bufferMs;
        metrics.maxBufferMs = Math.max(metrics.maxBufferMs, bufferMs);

        trackPlaybackEvent(
            ANALYTICS_EVENT_TYPES.VIDEO_BUFFERING,
            event.currentTarget,
            { bufferMs }
        );
    }, [trackPlaybackEvent]);

    const handleWaiting = useCallback(() => {
        const metrics = playbackPerformanceRef.current;
        if (metrics.firstPlayingAtMs === null || metrics.bufferStartedAtMs !== null) {
            return;
        }

        metrics.bufferStartedAtMs = nowMs();
    }, []);

    const handlePlaybackError = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        setIsVideoPlaybackActive(false);
        trackPlaybackEvent(
            ANALYTICS_EVENT_TYPES.VIDEO_PLAYBACK_ERROR,
            event.currentTarget,
            { errorCode: event.currentTarget.error?.code ?? null }
        );

        if (isPlaybackSourceExpired(currentPlaybackSource)) {
            void recoverPlaybackSource(event.currentTarget, !event.currentTarget.paused);
            return;
        }

        setPlaybackSource(null);
        setPlaybackSourceStatus('error');
        setPlaybackSourceMessage('Playback stopped unexpectedly. Please try again.');
    }, [currentPlaybackSource, recoverPlaybackSource, trackPlaybackEvent]);

    const handleSeeking = useCallback(() => {
        const metrics = playbackPerformanceRef.current;
        if (metrics.firstPlayingAtMs === null || metrics.seekingStartedAtMs !== null) {
            return;
        }

        metrics.seekingStartedAtMs = nowMs();
    }, []);

    const handleSeeked = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const metrics = playbackPerformanceRef.current;
        if (metrics.seekingStartedAtMs === null) {
            return;
        }

        const seekMs = roundMetric(nowMs() - metrics.seekingStartedAtMs);
        metrics.seekingStartedAtMs = null;
        trackPlaybackEvent(
            ANALYTICS_EVENT_TYPES.VIDEO_SEEKED,
            event.currentTarget,
            { seekMs }
        );
    }, [trackPlaybackEvent]);

    const handlePlay = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
        const metrics = playbackPerformanceRef.current;
        if (metrics.firstPlayingAtMs === null) {
            // PlaybackPerformance: startup should measure from the user's play request, not page load or idle time.
            metrics.startupStartedAtMs = nowMs();
        }

        if (isPlaybackSourceExpired(currentPlaybackSource)) {
            event.currentTarget.pause();
            void recoverPlaybackSource(event.currentTarget, true);
            return;
        }

        setIsVideoPlaybackActive(true);
        setIsMiniPlayerDismissed(false);
    }, [currentPlaybackSource, recoverPlaybackSource]);

    const handlePause = useCallback(() => {
        setIsVideoPlaybackActive(false);
    }, []);

    const handleRestartPlayback = useCallback(() => {
        const video = playerStageRef.current?.querySelector('video');
        const videoId = analyticsVideoIdRef.current;
        if (!video || !videoId) {
            return;
        }

        const restartFromSeconds = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const duration = Number.isFinite(video.duration)
            ? video.duration
            : watchStateRef.current.durationSeconds;

        // Restart is analytics-only; the persisted resume point changes at the next normal watch checkpoint.
        void trackEvent({
            eventType: ANALYTICS_EVENT_TYPES.VIDEO_RESTARTED,
            videoId,
            positionSeconds: restartFromSeconds,
            durationSeconds: duration || undefined,
            sessionId: analyticsSessionId,
            path: `${window.location.pathname}${window.location.search}`,
            metadata: buildPlaybackPerformanceMetadata(video, {
                restartFromSeconds: roundMetric(restartFromSeconds),
                resumeSeconds: parseResumeSeconds(resumeSecondsParam) ?? null
            })
        });

        const existingDurationSeconds = watchStateRef.current.durationSeconds;
        watchStateRef.current = {
            hasStarted: false,
            lastProgressBucket: 0,
            durationSeconds: existingDurationSeconds
        };
        playbackPerformanceRef.current = createPlaybackPerformanceState();
        video.currentTime = 0;
        setCanRestartFromResume(false);
        void video.play().catch(() => undefined);
    }, [analyticsSessionId, buildPlaybackPerformanceMetadata, resumeSecondsParam, trackEvent]);

    useEffect(() => {
        const requestId = accessRequestIdRef.current + 1;
        accessRequestIdRef.current = requestId;
        let isCurrentRequest = true;

        const canApplyRequest = () => isCurrentRequest
            && accessRequestIdRef.current === requestId
            && accessDecisionKeyRef.current === accessDecisionKey;

        const fetchVideoData = async () => {
            if (isUserStateLoading || isPurchaseLoading || isSubscriptionLoading) {
                return;
            }

            const userInfo = getUserInfo();

            if (!isLoggedIn || !userInfo) {
                localStorage.setItem('redirectAfterLogin', window.location.href);
                openModal('paid_content', window.location.href);
                return;
            }

            setIsCheckingAccess(true);

            try {
                const userRoles = userInfo.roles ?? [];
                const isAdminUser = isAdmin(userRoles);

                const hasSubscriptionAccess = isAdminUser || hasActiveSubscription;

                const applyAccessState = (ownsVideo: boolean, canAccess: boolean) => {
                    if (!canApplyRequest()) {
                        return;
                    }

                    const entitlementMarker = buildEntitlementMarker(
                        userInfo.token,
                        userRoles,
                        hasSubscriptionAccess,
                        ownsVideo
                    );
                    entitlementMarkerRef.current = entitlementMarker;
                    setAccessContextKey(accessDecisionKey);

                    const deniedState = playbackSourceDeniedRef.current;
                    if (deniedState?.videoId === currentVideoId) {
                        if (deniedState.entitlementMarker === entitlementMarker) {
                            setHasAccess(false);
                            return;
                        }

                        // A 403 only suppresses retries for the same entitlement state.
                        // Purchase/subscription/user changes should allow same-page recovery.
                        playbackSourceDeniedRef.current = null;
                    }

                    setHasAccess(canAccess);
                };

                if (currentVideoId) {
                    if (selectedVideo?.Url !== currentVideoId) {
                        setSelectedVideo(null);
                        const videoDetails = await fetchVideoDetails(currentVideoId);
                        if (!canApplyRequest()) {
                            return;
                        }
                        setSelectedVideo(videoDetails);
                        const ownsVideo = isVideoOwned(videoDetails.Id);
                        const canAccess = hasSubscriptionAccess || ownsVideo;
                        applyAccessState(ownsVideo, canAccess);
                    } else {
                        const ownsVideo = ownsSelectedVideo;
                        const canAccess = hasSubscriptionAccess || ownsVideo;
                        applyAccessState(ownsVideo, canAccess);
                    }
                }
            } catch {
                if (canApplyRequest()) {
                    setHasAccess(false);
                    setAccessContextKey(accessDecisionKey);
                    toast.error("Failed to verify access. Please try again later.");
                }
            } finally {
                if (canApplyRequest()) {
                    setIsCheckingAccess(false);
                }
            }
        };

        void fetchVideoData();

        return () => {
            isCurrentRequest = false;
        };
    }, [
        getUserInfo,
        isUserStateLoading,
        isLoggedIn,
        currentVideoId,
        openModal,
        selectedVideo?.Url,
        selectedVideo?.Id,
        isPurchaseLoading,
        isSubscriptionLoading,
        hasActiveSubscription,
        isVideoOwned,
        ownsSelectedVideo,
        accessDecisionKey,
        playbackContextKey,
        playbackToken,
        playbackUserRolesKey
    ]);

    useEffect(() => {
        const fetchRelated = async () => {
            if (!selectedVideo?.Url || !currentHasAccess) {
                setRelatedVideos([]);
                setIsRelatedLoading(false);
                return;
            }

            setIsRelatedLoading(true);

            try {
                let candidates: Video[] = [];

                if (hasSearchContext) {
                    const response = await searchVideos(searchQuery, 1, UP_NEXT_PAGE_SIZE, searchTags);
                    candidates = response.Results;
                } else {
                    const normalizedKey = (value?: string | null) => value?.trim().toLowerCase() ?? '';
                    const queryTerms = new Map<string, string>();

                    if (selectedVideo.EventName) {
                        const key = normalizedKey(selectedVideo.EventName);
                        if (key) {
                            queryTerms.set(key, selectedVideo.EventName);
                        }
                    }

                    if (selectedVideo.LocationName) {
                        const key = normalizedKey(selectedVideo.LocationName);
                        if (key) {
                            queryTerms.set(key, selectedVideo.LocationName);
                        }
                    }

                    participantNames.slice(0, MAX_PARTICIPANT_QUERY_COUNT).forEach((name) => {
                        const key = normalizedKey(name);
                        if (key) {
                            queryTerms.set(key, name);
                        }
                    });

                    const requests = Array.from(queryTerms.values())
                        .map((term) => searchVideos(term, 1, UP_NEXT_PAGE_SIZE));

                    if (combinedTags.length > 0) {
                        requests.push(searchVideos('', 1, UP_NEXT_PAGE_SIZE, combinedTags.slice(0, MAX_TAG_QUERY_COUNT)));
                    }

                    if (requests.length === 0) {
                        requests.push(searchVideos('', 1, UP_NEXT_PAGE_SIZE));
                    }

                    const responses = await Promise.all(requests);
                    candidates = responses.flatMap((response) => response.Results);
                }

                const uniqueCandidates = new Map<string, Video>();
                candidates.forEach((video) => {
                    const key = video.Id || video.Url;
                    if (key && !uniqueCandidates.has(key)) {
                        uniqueCandidates.set(key, video);
                    }
                });

                const filtered = Array.from(uniqueCandidates.values()).filter(
                    (video) => video.Url !== selectedVideo.Url && video.Id !== selectedVideo.Id
                );

                if (!hasSearchContext) {
                    const normalized = (value?: string | null) => value?.trim().toLowerCase() ?? '';
                    const eventKey = normalized(selectedVideo.EventName);
                    const participantKeys = new Set(participantNames.map(normalized));
                    const tagKeys = new Set(combinedTags.map(normalized));

                    const scoreCandidate = (video: Video) => {
                        let score = 0;

                        if (eventKey && normalized(video.EventName) === eventKey) {
                            score += 100;
                        }

                        const candidateParticipants = video.Participants?.map((participant) => normalized(participant.ParticipantName)) ?? [];
                        const sharedParticipants = candidateParticipants.filter((name) => name && participantKeys.has(name));
                        score += Math.min(sharedParticipants.length, 2) * 10;

                        if (tagKeys.size > 0) {
                            const candidateTags = [
                                ...(video.EventTags ?? []),
                                ...(video.Tags ?? [])
                            ].map((tag) => normalized(tag));
                            const sharedTags = candidateTags.filter((tag) => tag && tagKeys.has(tag));
                            score += Math.min(sharedTags.length, 3) * 5;
                        }

                        return score;
                    };

                    const sorted = filtered
                        .map((video) => ({ video, score: scoreCandidate(video) }))
                        .sort((left, right) => {
                            if (left.score !== right.score) {
                                return right.score - left.score;
                            }

                            const leftDate = Date.parse(left.video.CreatedDate ?? '') || 0;
                            const rightDate = Date.parse(right.video.CreatedDate ?? '') || 0;
                            return rightDate - leftDate;
                        })
                        .map((entry) => entry.video);

                    setRelatedVideos(sorted.slice(0, UP_NEXT_LIMIT));
                } else {
                    setRelatedVideos(filtered.slice(0, UP_NEXT_LIMIT));
                }
            } catch (error) {
                setRelatedVideos([]);
            } finally {
                setIsRelatedLoading(false);
            }
        };

        fetchRelated();
    }, [
        selectedVideo?.Url,
        selectedVideo?.Id,
        selectedVideo?.EventName,
        selectedVideo?.LocationName,
        currentHasAccess,
        hasSearchContext,
        searchQuery,
        searchTags,
        participantNames,
        combinedTags
    ]);

    const handleRelatedSelect = (video: VideoCardData) => {
        const matchedVideo = relatedVideos.find((item) => item.Id === video.id);
        if (matchedVideo) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('v', matchedVideo.Url);
            const targetResumeSeconds = readResumeSecondsFromWatchUrl(video.watchUrl);
            if (targetResumeSeconds !== null && targetResumeSeconds > 0) {
                nextParams.set('t', String(Math.floor(targetResumeSeconds)));
            } else {
                nextParams.delete('t');
            }
            setSearchParams(nextParams);
        }
    };

    if (!currentVideoId) {
        return (
            <div className="flex items-center justify-center h-screen text-xl">
                Not Found
            </div>
        );
    }

    if (!selectedVideo || currentIsCheckingAccess) {
        return null;
    }

    const videoCardData = selectedVideo ? buildVideoCardData(selectedVideo) : null;
    const relatedVideoCards = relatedVideos.map(buildVideoCardData);
    const orderedParticipants = orderVideoCardParticipants(videoCardData?.participants ?? []);
    const leftParticipant = orderedParticipants[0];
    const rightParticipant = orderedParticipants[1];
    const leftName = formatVideoCardParticipantName(leftParticipant?.name);
    const rightName = formatVideoCardParticipantName(rightParticipant?.name);
    const eventDateLabel = formatVideoCardDate(videoCardData?.eventDate);
    const fightNumberLabel = formatVideoCardFightNumber(videoCardData?.fightNumber);
    const metaItems = [fightNumberLabel, eventDateLabel, videoCardData?.eventName, videoCardData?.venueName].filter(
        (value) => Boolean(value && value.trim())
    ) as string[];
    const purchaseEnabled = selectedVideo.PurchaseEnabled ?? true;
    const isOwned = isVideoOwned(selectedVideo.Id);
    const showPurchasePanel = !currentHasAccess;

    const canSave = isValidGuid(selectedVideo.Id);
    const saved = isSaved(selectedVideo.Id);

    const handlePurchase = async () => {
        await startPurchase(selectedVideo.Id);
    };

    const railSuggestedCards = relatedVideoCards.slice(0, 4);
    const showWatchMomentum = currentHasAccess && (
        continueWatching.isLoading
        || continueWatching.items.length > 0
        || isRelatedLoading
        || railSuggestedCards.length > 0
    );
    const showSuggestedVideos = currentHasAccess && !isRelatedLoading && relatedVideoCards.length > 0;
    const eventDiscoveryItems = selectedVideo.EventName ? [selectedVideo.EventName] : [];
    const hasWatchDiscovery = watchDiscoveryTags.length > 0
        || participantNames.length > 0
        || eventDiscoveryItems.length > 0;
    const isFloatingPlayerActive = floatingPlayerMode !== 'none';
    const playerFrameClassName = [
        styles.playerFrame,
        floatingPlayerMode === 'desktop' ? styles.playerFrameMini : '',
        floatingPlayerMode === 'mobile' ? styles.playerFrameMobile : ''
    ].filter(Boolean).join(' ');
    const saveButtonClassName = [
        styles.saveButton,
        saved ? styles.saveButtonActive : ''
    ].filter(Boolean).join(' ');
    const posterUrl = selectedVideo.ThumbnailBase64
        ? `data:image/png;base64,${selectedVideo.ThumbnailBase64}`
        : undefined;
    const renderPurchasePanel = (className?: string) => (
        <div className={[styles.purchasePanel, className ?? ''].filter(Boolean).join(' ')}>
            <h2>Get instant access</h2>
            <p>
                Buy this fight once and watch it any time with your account.
            </p>
            <div className={styles.purchaseActions}>
                {purchaseEnabled ? (
                    <button
                        type="button"
                        className={styles.primaryAction}
                        onClick={handlePurchase}
                    >
                        Buy Now
                    </button>
                ) : (
                    <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => router.navigate(Routes.CONTACT)}
                    >
                        Contact us about purchase
                    </button>
                )}
                <button
                    type="button"
                    className={styles.secondaryAction}
                    onClick={() => router.navigate(buildJoinUrl({
                        offeringId: 'monthly',
                        source: 'paywall',
                        returnTo: `${window.location.pathname}${window.location.search}`
                    }))}
                >
                    Start watching
                </button>
            </div>
        </div>
    );

    return (
        <main className={styles.watchPage}>
            <div className={styles.watchShell}>
                <div className={styles.watchLayout}>
                    <section className={styles.watchIntro} aria-label="Video player">
                        <div
                            ref={playerStageRef}
                            className={styles.playerStage}
                            data-floating={floatingPlayerMode}
                        >
                            <div className={playerFrameClassName}>
                                {currentHasAccess && playerVideoUrl ? (
                                    <>
                                        <VideoPlayer
                                            key={playerVideoUrl}
                                            videoUrl={playerVideoUrl}
                                            type={playerContentType}
                                            className={styles.videoElement}
                                            poster={posterUrl}
                                            onLoadedMetadata={handleLoadedMetadata}
                                            onTimeUpdate={handleTimeUpdate}
                                            onEnded={handleEnded}
                                            onPlaying={handlePlaying}
                                            onWaiting={handleWaiting}
                                            onError={handlePlaybackError}
                                            onSeeking={handleSeeking}
                                            onSeeked={handleSeeked}
                                            onPlay={handlePlay}
                                            onPause={handlePause}
                                        />
                                        {isFloatingPlayerActive ? (
                                            <div className={styles.miniPlayerControls}>
                                                <button
                                                    type="button"
                                                    className={styles.miniPlayerButton}
                                                    aria-label="Return to main video"
                                                    onClick={() => {
                                                        playerStageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                        setFloatingPlayerMode('none');
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" aria-hidden="true">open_in_full</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.miniPlayerButton}
                                                    aria-label="Close mini player"
                                                    onClick={() => {
                                                        playerStageRef.current?.querySelector('video')?.pause();
                                                        setIsMiniPlayerDismissed(true);
                                                        setFloatingPlayerMode('none');
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" aria-hidden="true">close</span>
                                                </button>
                                            </div>
                                        ) : null}
                                        {canRestartFromResume && !isVideoPlaybackActive ? (
                                            <button
                                                type="button"
                                                className={styles.restartButton}
                                                aria-label="Restart from beginning"
                                                title="Restart from beginning"
                                                onClick={handleRestartPlayback}
                                            >
                                                <span className="material-symbols-outlined" aria-hidden="true">replay</span>
                                            </button>
                                        ) : null}
                                    </>
                                ) : currentHasAccess && (currentPlaybackSourceStatus === 'unavailable' || currentPlaybackSourceStatus === 'error') ? (
                                    <div className={styles.playbackIssue}>
                                        <span className="material-symbols-outlined" aria-hidden="true">error</span>
                                        <span>{currentPlaybackSourceMessage ?? 'Unable to prepare video.'}</span>
                                        <button
                                            type="button"
                                            className={styles.playbackRetryButton}
                                            onClick={() => {
                                                void loadPlaybackSource();
                                            }}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : currentHasAccess ? (
                                    <div className={styles.playerLoading}>Preparing video...</div>
                                ) : (
                                    <div className={styles.lockedPlayer}>
                                        <span className="material-symbols-outlined" aria-hidden="true">lock</span>
                                        <span>Unlock this video to watch</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {showPurchasePanel ? renderPurchasePanel(styles.mobilePurchasePanel) : null}
                    </section>

                    <aside className={styles.watchRail} aria-label="Fight details and discovery">
                        <div className={styles.videoDetails}>
                            <div className={styles.matchupWrap}>
                                <FighterCornerBars
                                    leftName={leftName}
                                    leftColor={leftParticipant?.cornerColor ?? null}
                                    rightName={rightName}
                                    rightColor={rightParticipant?.cornerColor ?? null}
                                    layout="row"
                                    size="md"
                                    stackOnMobile
                                />
                                {isOwned ? (
                                    <span className={styles.ownedBadge}>
                                        Owned
                                    </span>
                                ) : null}
                            </div>

                            <div className={styles.titleRow}>
                                <h1 className={styles.videoTitle}>{selectedVideo.Title}</h1>
                                {canSave ? (
                                    <button
                                        type="button"
                                        className={saveButtonClassName}
                                        aria-label={saved ? 'Remove from saved' : 'Save for later'}
                                        aria-pressed={saved}
                                        onClick={() => {
                                            void toggleSaved(selectedVideo.Id);
                                        }}
                                    >
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                            {saved ? 'bookmark' : 'bookmark_add'}
                                        </span>
                                    </button>
                                ) : null}
                            </div>

                            {metaItems.length > 0 ? (
                                <div className={styles.metaList} aria-label="Video details">
                                    {metaItems.map((item) => (
                                        <span key={item} className={styles.metaItem}>
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            ) : null}

                            {combinedTags.length > 0 ? (
                                <div className={styles.tagScroller} aria-label="Video tags">
                                    {combinedTags.map((tag) => (
                                        <span key={tag} className={styles.tagPill}>
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            ) : null}

                            {(selectedVideo?.Description ?? '').length > 0 ? (
                                <div className={styles.descriptionPanel}>
                                    {selectedVideo.Description}
                                </div>
                            ) : null}
                        </div>

                        {showWatchMomentum ? (
                            <WatchMomentumPanel
                                className={styles.watchMomentumPanel}
                                variant="sidebar"
                                continueItems={continueWatching.items}
                                suggestedVideos={railSuggestedCards}
                                isContinueLoading={continueWatching.isLoading}
                                isSuggestedLoading={isRelatedLoading}
                                continueTitle="Continue Watching"
                                continueSubtitle="Resume another recent fight."
                                suggestedTitle="Up Next"
                                suggestedSubtitle="Related fights from this viewing context."
                            />
                        ) : null}

                        {hasWatchDiscovery ? (
                            <div className={styles.watchDiscoveryPanel}>
                                <SearchDiscoveryPanels
                                    tags={watchDiscoveryTags}
                                    fighters={participantNames}
                                    events={eventDiscoveryItems}
                                    showVideos={false}
                                    showViewAll={false}
                                    onTagSelect={(tag) => {
                                        void router.navigate(buildTagResultsRoute(tag));
                                    }}
                                    onFighterSelect={(fighter) => {
                                        void router.navigate(buildTextResultsRoute(fighter));
                                    }}
                                    onEventSelect={(eventName) => {
                                        void router.navigate(buildTextResultsRoute(eventName));
                                    }}
                                />
                            </div>
                        ) : null}

                        {showPurchasePanel ? renderPurchasePanel(styles.desktopPurchasePanel) : null}
                    </aside>

                    {showSuggestedVideos ? (
                        <section className={styles.suggestionsSection} aria-label="Suggested videos">
                            <h2>More Suggested Videos</h2>
                            <RelatedVideos
                                videos={relatedVideoCards}
                                isLoading={false}
                                layout="grid"
                                onSelect={handleRelatedSelect}
                            />
                        </section>
                    ) : null}
                </div>
            </div>
        </main>
    );
}

export default WatchPage;
